import { useState } from 'react'
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, FormControl, RadioGroup, FormControlLabel, Radio, FormLabel, Alert } from '@mui/material'
import { useT } from '@/i18n/useT'
import { ExportError } from '@/hooks/useDatabase'

interface ExportDialogProps {
  open: boolean
  onClose: () => void
  onExport: (config: { format: 'xlsx' | 'csv'; separator: 'comma' | 'period'; includeCustomColumns: boolean }) => Promise<void>
}

export function ExportDialog({ open, onClose, onExport }: ExportDialogProps) {
  const { t } = useT()
  const [format, setFormat] = useState<'xlsx' | 'csv'>('xlsx')
  const [separator, setSeparator] = useState<'comma' | 'period'>('comma')
  const [includeCustomColumns, setIncludeCustomColumns] = useState(true)

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleExport = async () => {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      await onExport({ format, separator, includeCustomColumns })
    } catch (err) {
      const status = err instanceof ExportError ? err.status : -1
      console.error('[export] failed', status, err instanceof ExportError ? err.serverMessage : err)
      if (status === 401) setError(t('Your session has expired. Please log in again.'))
      else if (status === 0) setError(t('The server could not be reached.'))
      else setError(t('Export failed'))
    } finally {
      setBusy(false)
    }
  }

  const handleClose = () => {
    if (busy) return
    setError(null)
    // Reset to defaults
    setFormat('xlsx')
    setSeparator('comma')
    setIncludeCustomColumns(true)
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('Export database')}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ pt: 2 }}>
          <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
            <FormLabel component="legend">{t('File format')}</FormLabel>
            <RadioGroup
              row
              value={format}
              onChange={(e) => setFormat(e.target.value as 'xlsx' | 'csv')}
            >
              <FormControlLabel value="xlsx" control={<Radio />} label="Excel (.xlsx)" />
              <FormControlLabel value="csv" control={<Radio />} label="CSV" />
            </RadioGroup>
          </FormControl>

          {/* xlsx writes real numbers that Excel shows in the user's locale; the separator only applies to CSV */}
          {format === 'csv' && (
            <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
              <FormLabel component="legend">{t('Decimal separator')}</FormLabel>
              <RadioGroup
                row
                value={separator}
                onChange={(e) => setSeparator(e.target.value as 'comma' | 'period')}
              >
                <FormControlLabel
                  value="comma"
                  control={<Radio />}
                  label={`${t('Comma')} (1.234,56)`}
                />
                <FormControlLabel
                  value="period"
                  control={<Radio />}
                  label={`${t('Period')} (1,234.56)`}
                />
              </RadioGroup>
            </FormControl>
          )}

          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend">{t('Include custom columns')}</FormLabel>
            <RadioGroup
              row
              value={includeCustomColumns.toString()}
              onChange={(e) => setIncludeCustomColumns(e.target.value === 'true')}
            >
              <FormControlLabel value="true" control={<Radio />} label={t('Yes')} />
              <FormControlLabel value="false" control={<Radio />} label={t('No')} />
            </RadioGroup>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>{t('Cancel')}</Button>
        <Button onClick={handleExport} variant="contained" disabled={busy}>
          {busy ? t('Exporting …') : t('Export')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
